import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    TooltipItem,
} from 'chart.js';
import { ChildCountData } from '../../../services/network/form30SectorCharts-api';
import styles from './ChildCountChart.module.css';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

interface ChildCountChartProps {
    data: ChildCountData | null;
    sectorName?: string;
}

export const ChildCountChart: React.FC<ChildCountChartProps> = React.memo(({ data, sectorName }) => {
    const hasData = useMemo(() => {
        if (!data) return false;
        return Object.values(data).some(value => value !== null);
    }, [data]);

    // Подготовка данных для графика
    const { chartData, originalPlanValues, originalFactValues } = useMemo(() => {
        const planValues = data ? [
            data.age_0_4_plan || 0,
            data.age_5_9_plan || 0,
            data.age_10_14_plan || 0,
            data.age_15_17_plan || 0,
            data.age_0_14_plan || 0,
            data.age_0_17_plan || 0,
            data.disabled_0_17_plan || 0,
        ] : [0, 0, 0, 0, 0, 0, 0];

        const factValues = data ? [
            data.age_0_4_fact || 0,
            data.age_5_9_fact || 0,
            data.age_10_14_fact || 0,
            data.age_15_17_fact || 0,
            data.age_0_14_fact || 0,
            data.age_0_17_fact || 0,
            data.disabled_0_17_fact || 0,
        ] : [0, 0, 0, 0, 0, 0, 0];

        // Вычисляем данные для плана: показываем только разницу (план - факт), если она > 0
        const planDataForChart = planValues.map((plan, index) => {
            const fact = factValues[index];
            const diff = plan - fact;
            return diff > 0 ? diff : 0;
        });

        return {
            chartData: {
                labels: ['0-4 лет', '5-9 лет', '10-14 лет', '15-17 лет', '0-14 лет', '0-17 лет', 'Дети-инвалиды 0-17 лет'],
                datasets: [
                    {
                        label: 'План',
                        data: planDataForChart,
                        backgroundColor: 'rgba(245, 167, 66, 1)',
                        stack: 'stack1',
                        order: 2,
                    },
                    {
                        label: 'Факт',
                        data: factValues,
                        backgroundColor: 'rgba(26, 100, 165, 1)',
                        stack: 'stack1',
                        order: 1,
                    },
                ],
            },
            originalPlanValues: planValues,
            originalFactValues: factValues,
        };
    }, [data]);

    const options = {
        responsive: true,
        maintainAspectRatio: true,
        scales: {
            x: {
                stacked: true,
                grid: {
                    display: false,
                },
                ticks: {
                    font: {
                        size: 12,
                        weight: 'bold' as const,
                    },
                },
            },
            y: {
                stacked: true, // Включаем stacking для правильного наложения
                beginAtZero: true,
                grid: {
                    color: '#e0e0e0',
                },
                ticks: {
                    font: {
                        size: 11,
                    },
                },
            },
        },
        plugins: {
            legend: {
                display: true,
                position: 'top' as const,
                labels: {
                    usePointStyle: true,
                    pointStyle: 'rect',
                    padding: 15,
                    font: {
                        size: 13,
                        weight: 'bold' as const,
                    },
                },
                onClick: () => {}, // Отключаем скрытие баров при клике на легенду
            },
            tooltip: {
                mode: 'index' as const,
                intersect: false,
                callbacks: {
                    label: function(context: TooltipItem<'bar'>) {
                        const datasetIndex = context.datasetIndex;
                        const dataIndex = context.dataIndex;
                        
                        if (datasetIndex === 0) {
                            // План (первый датасет)
                            const value = originalPlanValues[dataIndex];
                            return `План: ${Math.ceil(value)}`;
                        } else {
                            // Факт (второй датасет)
                            const value = originalFactValues[dataIndex];
                            return `Факт: ${Math.ceil(value)}`;
                        }
                    }
                }
            }
        },
        barPercentage: 0.8,
        categoryPercentage: 0.9,
    };

    return (
        <div className={styles.chartContainer}>
            <h3 className={styles.chartTitle}>
                Охват профилактическими осмотрами детей
                {sectorName && <span className={styles.sectorName}> - {sectorName}</span>}
            </h3>
            <div className={styles.chartWrapper}>
                {hasData ? (
                    <Bar data={chartData} options={options} />
                ) : (
                    <div className={styles.noData}>
                        <p>Нет данных для построения графика</p>
                    </div>
                )}
            </div>
        </div>
    );
});
